// frontend/src/features/agencies/agenciesSlice.js
// This Redux slice manages agencies state—fetching/creating/deleting agencies, tracking live active-driver counts and limits, and exposing selectors for use across the app.
import { createEntityAdapter, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Thunk to fetch all agencies
export const fetchAgencies = createAsyncThunk(
  'agencies/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/agencies');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load agencies');
    }
  }
);

// Thunk to create a new agency
export const createAgency = createAsyncThunk(
  'agencies/create',
  async (name, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/ups-admin/agencies', { name });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create agency');
    }
  }
);

// Thunk to delete an agency
export const deleteAgency = createAsyncThunk(
  'agencies/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/ups-admin/agencies/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete agency');
    }
  }
);

// Entity adapter for normalized agency state
 const agenciesAdapter = createEntityAdapter({
   selectId: a => a.id,
   sortComparer: (a, b) => {
     const an = (a?.name ?? a?.agency_name ?? '').toString();
     const bn = (b?.name ?? b?.agency_name ?? '').toString();
     return an.localeCompare(bn);
   },
 });

// Initial state with adapter
const initialState = agenciesAdapter.getInitialState({
  loading: false,
  error: null,
});

const agenciesSlice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {
    // Real-time slice updates
    addAgency: agenciesAdapter.addOne,
    removeAgency: agenciesAdapter.removeOne,

    // Increment/decrement active driver count for live updates
    incrementActiveCount: (state, action) => {
      const id = action.payload;
      if (state.entities[id]) {
        state.entities[id].current_active_count = (state.entities[id].current_active_count || 0) + 1;
      }
    },
    decrementActiveCount: (state, action) => {
      const id = action.payload;
      if (state.entities[id]) {
        state.entities[id].current_active_count = Math.max((state.entities[id].current_active_count || 0) - 1, 0);
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch agencies
      .addCase(fetchAgencies.pending, state => {
        state.loading = true;
        state.error = null;
      })
       .addCase(fetchAgencies.fulfilled, (state, action) => {
        state.loading = false;
        const normalized = Array.isArray(action.payload)
          ? action.payload.map(a => ({
            id: a.id ?? a.agency_id,
            name: a.name ?? a.agency_name ?? '(Unnamed)',
            current_active_count: a.current_active_count ?? 0,
            max_active_drivers: a.max_active_drivers ?? a.limit ?? 0,
         ...a,
       }))
     : [];
      agenciesAdapter.setAll(state, normalized);
      })
      .addCase(fetchAgencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create agency
      .addCase(createAgency.fulfilled, (state, action) => {
        agenciesAdapter.addOne(state, action.payload);
      })
      .addCase(createAgency.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete agency
      .addCase(deleteAgency.fulfilled, (state, action) => {
        agenciesAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteAgency.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  addAgency,
  removeAgency,
  incrementActiveCount,
  decrementActiveCount,
} = agenciesSlice.actions;

export default agenciesSlice.reducer;

// Export selectors: selectAllAgencies, selectAgencyById
export const {
  selectAll: selectAllAgencies,
  selectById: selectAgencyById,
} = agenciesAdapter.getSelectors(state => state.agencies);

// Selector for driver limit enforcement
export const selectLimitByAgency = (state, agencyId) =>
  state.agencies.entities[agencyId]?.max_active_drivers ?? 0;

// **New**: live active-driver count selector
export const selectActiveCountByAgency = (state, agencyId) =>
  state.agencies.entities[agencyId]?.current_active_count ?? 0;

// Selector for whether we’re currently loading agencies
export const selectAgenciesLoading = state => state.agencies.loading;
